import { useCallback, useSyncExternalStore } from 'react';
import { useQuery, type InfiniteData } from '@tanstack/react-query';
import { API_BASE } from './api-base';
import { apiFetch } from './api-client';
import { queryClient, queryKeys } from './query-client';
import type { Conversation } from './conversations';
import type { ChatListPage, ChatListRow } from './chats';
import { useObservedQuery } from './use-observed-query';

// The conversation the user is IN, whether or not the sidebar list holds it.
//
// Everything around a chat reads its ROW — the header title, the header's pin / rename / delete, the recap
// above the composer — and the only source of rows was the sidebar list, which is one page of the account's
// conversations. So a chat the list does not hold lost all of it: the header read "New Symphony chat" and
// its menu dropped the three actions, on a conversation that has them. The list is still the fast path;
// this fetches the row only when the list cannot answer.
//
// A Symphony GROUP chat adds a second list: the Chats page's `chats-v1` rows are the only ones that carry the
// `group` summary and a joined member's `viewerState` — and a group the viewer JOINED lives in its owner's account,
// so neither the sidebar list nor the by-id read can hold it at all. The chats-v1 row is therefore folded in.

async function fetchConversation(conversationId: string): Promise<Conversation | null> {
  const res = await apiFetch(`${API_BASE}/api/conversations/${encodeURIComponent(conversationId)}`);
  if (!res.ok) return null;
  const body = (await res.json()) as { conversation?: Conversation | null };
  return body.conversation ?? null;
}

/** The chats-v1 row for this conversation, from whichever Chats query holds it (no fetch of its own). */
export function findChatsRow(conversationId: string | undefined): ChatListRow | undefined {
  if (!conversationId) return undefined;
  for (const [, data] of queryClient.getQueriesData<InfiniteData<ChatListPage>>({ queryKey: queryKeys.chatsAll })) {
    for (const page of data?.pages ?? []) {
      const row = page.conversations.find((r) => r.id === conversationId);
      if (row) return row;
    }
  }
  return undefined;
}

export function useChatsRow(conversationId: string | undefined): ChatListRow | undefined {
  const subscribe = useCallback((onChange: () => void) => queryClient.getQueryCache().subscribe(onChange), []);
  const read = useCallback(() => findChatsRow(conversationId), [conversationId]);
  return useSyncExternalStore(subscribe, read, () => undefined);
}

/**
 * `fromList` when the list holds the conversation, else the row fetched by id, else the chats-v1 row. A group
 * summary the chats-v1 row carries is folded onto the first two, which do not carry one. Returns undefined while
 * there is no conversation to speak of (a brand-new chat), which is what keeps the row-scoped actions absent
 * rather than present and inert.
 */
export function useConversationRow(
  conversationId: string | undefined,
  fromList: Conversation | undefined,
): Conversation | undefined {
  // Disabled the moment the list can answer — the list is refreshed by the sidebar and by every write,
  // so a fetched copy must never be the one that wins.
  const enabled = Boolean(conversationId) && !fromList;
  const query = useQuery({
    queryKey: queryKeys.conversation(conversationId ?? undefined),
    queryFn: () => fetchConversation(conversationId as string),
    enabled,
  });
  const chatsRow = useChatsRow(conversationId);
  const base = fromList ?? query.data ?? undefined;
  if (!base) return chatsRow;
  if (chatsRow && chatsRow.group !== undefined && base.group === undefined) {
    return { ...base, group: chatsRow.group, ...(chatsRow.viewerState !== undefined ? { viewerState: chatsRow.viewerState } : {}) };
  }
  return base;
}

/** True once the by-id read answered that this account cannot read the conversation (a group in someone else's account). */
export function useConversationRowMissing(conversationId: string | undefined): boolean {
  // On the singleton client, not `useQuery`: the chat header (which asks this through `useGroupSummary`) also renders
  // with no QueryClientProvider above it.
  const query = useObservedQuery({
    queryKey: queryKeys.conversation(conversationId ?? undefined),
    queryFn: () => fetchConversation(conversationId as string),
    enabled: false,
  });
  return query.data === null;
}
